/* eslint-disable react/display-name */
import React, { HTMLProps, useCallback, useMemo, useState } from "react";
import { FiCalendar, FiSearch } from "react-icons/fi";
import classNames from "classnames";
import { InputProps } from "./input.props";

export const Input: React.FunctionComponent<
  InputProps & HTMLProps<HTMLInputElement>
> = React.forwardRef(
  (
    {
      value,
      onChange,
      placeholder,
      icon,
      onIconClick,
      invalid,
      LeftComponent,
      inputSize,
      usingCurrency,
      className,
      inputClassName,
      style,
      error,
      material,
      label,
      hint,
      enableCounter,
      maxChar,
      ...rest
    },
    ref
  ) => {
    const [focus, setFocus] = useState<boolean>(false);
    const [limitReached, setLimitReached] = useState<boolean>(false);
    const iconComponent = useMemo(() => {
      if (!icon) {
        return null;
      }
      switch (icon) {
        case "search":
          return <FiSearch className="input-icon-right" />;
        case "calendar":
          return <FiCalendar className="input-icon-right" />;
        case "close":
          return (
            <button
              onClick={onIconClick}
              type="button"
              className="close"
              aria-label="Close"
              style={{
                right: invalid ? 36 : 26,
                position: "absolute",
                top: 6,
                zIndex: 2,
                width: 18,
                height: 18,
              }}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          );
        default:
          return null;
      }
    }, [icon, invalid, onIconClick]);

    const handleChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>) => {
        const isLimitReached = maxChar && event.target.value?.length >= maxChar;
        setLimitReached(isLimitReached);
        if (isLimitReached) {
          event.target.value = event.target.value.substring(0, maxChar);
        }
        onChange(event);
      },
      [maxChar, onChange]
    );

    return (
      <div className={`flex flex-col ${className}`}>
        {!!label && (
          <small className="font-bold text-secondary-gray">{label}</small>
        )}
        <div className="w-full flex items-center mb-0">
          {usingCurrency && (
            <span className="text-black text-opacity-50 material-input flex items-center">
              Rp
            </span>
          )}
          {LeftComponent}
          <input
            ref={ref}
            value={value}
            placeholder={placeholder}
            onChange={handleChange}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            style={{ paddingRight: invalid ? 56 : 12, ...style }}
            className={classNames(
              "form-control text-body1",
              inputClassName,
              { "is-invalid": !!invalid },
              { "py-6": inputSize === "bigger" },
              { "border border-danger": error },
              { "material-input": !focus && material },
              { "highlight-input-border": focus && material }
            )}
            {...rest}
          />
          {iconComponent}
        </div>
        {(!!hint || enableCounter) && (
          <div className="flex justify-between">
            {!!hint && (
              <small className="text-black text-opacity-50">{hint}</small>
            )}
            {enableCounter && (
              <small
                className={`${
                  limitReached ? "text-orange" : "text-black text-opacity-50"
                } text-right`}
              >
                {value?.length ?? 0}/{maxChar}
              </small>
            )}
          </div>
        )}
      </div>
    );
  }
);
